/**
 * SkipButton — мелкая "незаметная" кнопка пропуска онбординга.
 *
 * По ТЗ админа (17 Apr 2026): "можно скип, но написать что
 * рекомендация пройти, кнопка оч мелко незаметно".
 *
 * Поведение:
 *   - В правом верхнем углу экрана, opacity 0.35
 *   - Размер текста 10px, tracking wide, uppercase
 *   - При hover/tap — opacity растёт до 0.7
 *   - При tap срабатывает onSkip с haptic warning
 *   - Мини-лейбл ниже: "рекомендуем пройти"
 */
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { SPRING_TAP } from './springs';
import { useSounds } from './useSounds';

interface SkipButtonProps {
  onSkip: () => void;
  /** Доступно только с N-го шага (чтобы на 1-м юзер не мог сразу скипнуть) */
  visible?: boolean;
}

export function SkipButton({ onSkip, visible = true }: SkipButtonProps) {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const sounds = useSounds();

  if (!visible) return null;

  return (
    <motion.button
      type="button"
      onClick={() => {
        haptic.warning();
        sounds.tap();
        onSkip();
      }}
      className="absolute top-[14px] right-[14px] z-50 flex flex-col items-end gap-[2px] text-right select-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.35 }}
      whileHover={{ opacity: 0.75 }}
      whileTap={{ scale: 0.94, opacity: 0.85 }}
      transition={SPRING_TAP}
      style={{
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '6px 10px',
        WebkitTapHighlightColor: 'transparent',
      }}
      aria-label={t('onboarding.skip.aria')}
    >
      <span
        className="font-mono"
        style={{
          fontSize: 10,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}
      >
        {t('onboarding.skip.label')}
      </span>
      <span
        style={{
          fontSize: 8,
          letterSpacing: '0.08em',
          color: 'var(--text-muted)',
          opacity: 0.6,
          fontWeight: 400,
        }}
      >
        {t('onboarding.skip.hint')}
      </span>
    </motion.button>
  );
}
